/**
 * Selection Utilities
 * Functions for managing text selection and caret position
 */

/**
 * Get the caret position (character offset) within a contenteditable element
 * @param {HTMLElement} element - The container element
 * @returns {number} Caret offset from start of element
 */
export function getCaretPosition(element) {
  let caretOffset = 0;
  const doc = element.ownerDocument || element.document;
  const win = doc.defaultView || doc.parentWindow;

  if (typeof win.getSelection !== 'undefined') {
    const sel = win.getSelection();
    if (sel.rangeCount > 0) {
      const range = win.getSelection().getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      caretOffset = preCaretRange.toString().length;
    }
  } else if (doc.selection && doc.selection.type !== 'Control') {
    const textRange = doc.selection.createRange();
    const preCaretTextRange = doc.body.createTextRange();
    preCaretTextRange.moveToElementText(element);
    preCaretTextRange.setEndPoint('EndToEnd', textRange);
    caretOffset = preCaretTextRange.text.length;
  }

  return caretOffset;
}

/**
 * Get the start and end positions of the current selection
 * @param {HTMLElement} containerElement - The container element for offset calculation
 * @returns {{ start: number, end: number, length: number } | null}
 */
export function getSelectionRange(containerElement) {
  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const length = range.toString().length;

  // Get the end position (caret position)
  const endPosition = getCaretPosition(containerElement);
  const startPosition = endPosition - length;

  return {
    start: Math.max(0, startPosition),
    end: endPosition,
    length
  };
}

/**
 * Check if there's an active text selection (not just a caret)
 * @returns {boolean}
 */
export function hasActiveSelection() {
  const selection = window.getSelection();
  return selection && selection.type === 'Range' && selection.toString().length > 0;
}

/**
 * Get selection range along with style IDs found within
 * @param {HTMLElement} containerElement - The container element
 * @param {Array} charStyleOverrides - Current charStyleOverrides array
 * @returns {{ range: [number, number], styleIds: Array, selectionRange: { start: number, end: number } } | null}
 */
export function getSelectionWithStyles(containerElement, charStyleOverrides = []) {
  const selRange = getSelectionRange(containerElement);

  if (!selRange || selRange.length === 0) {
    return null;
  }

  // Collect unique style IDs in the selection
  const styleIds = [];
  for (let i = selRange.start; i < selRange.end; i++) {
    const styleId = charStyleOverrides[i];
    if (styleId && styleId !== 0 && !styleIds.includes(styleId)) {
      styleIds.push(styleId);
    }
  }

  return {
    range: [selRange.start, selRange.end],
    styleIds,
    selectionRange: { start: selRange.start, end: selRange.end }
  };
}

/**
 * Set caret position within a contenteditable element
 * @param {HTMLElement} element - The contenteditable element
 * @param {number} position - Character position to set caret at
 */
export function setCaretPosition(element, position) {
  const range = document.createRange();
  const sel = window.getSelection();

  // Walk through text nodes to find the right position
  let currentPos = 0;
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let node = walker.nextNode();
  while (node) {
    const nodeLength = node.textContent.length;
    if (currentPos + nodeLength >= position) {
      // Found the right node
      range.setStart(node, position - currentPos);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    currentPos += nodeLength;
    node = walker.nextNode();
  }

  // If position is beyond text, set to end
  range.selectNodeContents(element);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Find the node and offset for a given character position
 * @param {HTMLElement} element - The container element
 * @param {number} position - Character position to find
 * @returns {{ node: Node, offset: number } | null}
 */
function findNodeAtPosition(element, position) {
  let currentPos = 0;
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let node = walker.nextNode();
  while (node) {
    const nodeLength = node.textContent.length;
    if (currentPos + nodeLength >= position) {
      return { node, offset: position - currentPos };
    }
    currentPos += nodeLength;
    node = walker.nextNode();
  }

  // Position beyond text - return last node at end
  if (node) {
    return { node, offset: node.textContent.length };
  }
  return null;
}

/**
 * Set a selection range within a contenteditable element
 * @param {HTMLElement} element - The contenteditable element
 * @param {number} start - Start character position
 * @param {number} end - End character position
 * @returns {boolean} Whether the selection was successfully set
 */
export function setSelectionRange(element, start, end) {
  if (!element || start < 0 || end < start) {
    return false;
  }

  const range = document.createRange();
  const sel = window.getSelection();

  const startPoint = findNodeAtPosition(element, start);
  const endPoint = findNodeAtPosition(element, end);

  if (!startPoint || !endPoint) {
    return false;
  }

  try {
    range.setStart(startPoint.node, startPoint.offset);
    range.setEnd(endPoint.node, endPoint.offset);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch (e) {
    console.warn('setSelectionRange failed:', e);
    return false;
  }
}
